// Aviso de horas de servicio antes de confirmar un presupuesto o de sacar la
// orden de cocina nueva (M1, 16-oct-2026): lista concreta de lo que falta o
// no cuadra ("Lunch (día 2, 18/11) sin hora") con "Poner las horas" y
// "Seguir igualmente". Nunca bloquea (decisiones del 30-sep y 02-oct): sin
// avisos, o si la consulta falla, se sigue sin preguntar.
//
// Uso:
//   const { revisarHorarios, dialogoHorarios } = useAvisoHorarios();
//   const decision = await revisarHorarios(id, { accion: 'confirmar', jornadaId });
//   // 'seguir' | 'poner' | 'cancelar'
//   ...
//   {dialogoHorarios}
import React, { useCallback, useRef, useState } from 'react';
import { Clock } from 'lucide-react';
import { presupuestosAPI } from '../../api/api';
import usePuedeEditarHorarios from '../../hooks/usePuedeEditarHorarios';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';
import { revisarNovedades } from '../guia/novedades';

const MAX_LISTA = 12;

const TEXTO_ACCION = {
  confirmar: 'Puedes confirmar igualmente: la orden de cocina y la hoja de material de esos días saldrán sin esas horas.',
  orden: 'La orden de cocina sale igualmente, pero sin esas horas.',
};

export const useAvisoHorarios = () => {
  const puedeEditar = usePuedeEditarHorarios();
  const [estado, setEstado] = useState(null); // { avisos, accion, poner }
  const resolverRef = useRef(null);

  const cerrar = useCallback((decision) => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setEstado(null);
    if (resolver) resolver(decision);
  }, []);

  // Devuelve 'seguir' si no hay nada que avisar (o la consulta falla)
  const revisarHorarios = useCallback(async (presupuestoId, { accion = 'confirmar', jornadaId = null } = {}) => {
    if (!presupuestoId) return 'seguir';
    let avisos = [];
    try {
      const { data } = await presupuestosAPI.avisosHorarios(presupuestoId, jornadaId);
      avisos = Array.isArray(data?.avisos) ? data.avisos : [];
    } catch (_e) {
      return 'seguir';
    }
    if (avisos.length === 0) return 'seguir';
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setEstado({ avisos, accion });
      // La ventana no cambia de ruta: que se mire si toca su tarjeta de novedad
      setTimeout(revisarNovedades, 300);
    });
  }, []);

  const avisos = estado?.avisos || [];
  const dialogoHorarios = estado ? (
    <Dialog open onOpenChange={(abierto) => { if (!abierto) cerrar('cancelar'); }}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-lg max-h-[85vh] overflow-y-auto bg-white"
        data-testid="aviso-horarios">
        <DialogTitle className="flex items-center gap-2 text-[#7A3A06]">
          <Clock className="w-5 h-5" />
          Revisa las horas de los servicios
        </DialogTitle>
        <DialogDescription className="text-sm text-[#33443C]">
          {TEXTO_ACCION[estado.accion] || TEXTO_ACCION.confirmar}
        </DialogDescription>
        <ul className="rounded-lg border border-[#F4D9AE] bg-[#FDF0DC] px-3 py-2 text-sm text-[#7A3A06] space-y-1"
          data-testid="aviso-horarios-lista">
          {avisos.slice(0, MAX_LISTA).map((a, i) => (
            <li key={`${a.jornada_id || ''}-${a.indice}-${a.tipo}-${i}`}>{a.texto}</li>
          ))}
          {avisos.length > MAX_LISTA && <li>Y {avisos.length - MAX_LISTA} más.</li>}
        </ul>
        {!puedeEditar && (
          <p className="text-xs text-[#5B6B64]">Las horas las pone quien gestiona el presupuesto: avísale.</p>
        )}
        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <button type="button" onClick={() => cerrar('seguir')}
            className="px-4 py-2 bg-white text-[#16221D] border border-[#CBD3CF] rounded-lg hover:bg-gray-50 text-sm font-semibold"
            data-testid="aviso-horarios-seguir">
            Seguir igualmente
          </button>
          {puedeEditar && (
            <button type="button" onClick={() => cerrar('poner')}
              className="px-4 py-2 bg-duetto-primary text-white rounded-lg hover:bg-opacity-90 text-sm font-semibold"
              data-testid="aviso-horarios-poner">
              Poner las horas
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  ) : null;

  return { revisarHorarios, dialogoHorarios };
};

export default useAvisoHorarios;
